import { components } from "@certn/api/generated-schemas/client-portal";
import {
  DialogTrigger,
  GhostIconButton,
  Popover,
  Space,
  Stack,
  Text,
  mq,
} from "@certn/cascadia-react";
import { Comments } from "@certn/cascadia-react/patterns/comments/Comments";
import { parseName } from "@certn/utils";
import { css } from "@emotion/css";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { CommentForm } from "./CommentForm";

interface CommentSectionProps {
  comments: readonly components["schemas"]["CaseComment"][];
  mutate: any;
}

export function CommentSection(props: CommentSectionProps) {
  const { comments, mutate } = props;
  const { t } = useTranslation();
  const commentDisclaimer = t(
    "Comments can be seen by anyone with access to this case within your organization, as well as Certn. The applicant will not see these comments."
  );
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "auto" });
    }
  }, [comments]);
  return (
    <Stack className={styles.sidebarContainer} gap="space-4">
      <div className={styles.headerSection}>
        <Text style="global/heading-large" color="text-base">
          {t("Comments")}
        </Text>
        <DialogTrigger>
          <GhostIconButton
            icon="info"
            testID="comment-info-button"
            aria-label={t("Information about comments button")}
          />
          <Popover placement="left">{commentDisclaimer}</Popover>
        </DialogTrigger>
      </div>

      <div className={styles.contentSection}>
        {comments.length > 0 ? (
          comments?.map((comment: any, i) => {
            return (
              <Comments
                key={i}
                name={parseName(comment.author.auth0_full_name)}
                dateAdded={comment.created}
                commentContent={comment.message}
              />
            );
          })
        ) : (
          <Text style="form/body-secondary" testID="no-comments-yet-text">
            {t("No comments yet.")}
          </Text>
        )}
        <div ref={bottomRef} />
      </div>
      <Space />
      <CommentForm mutate={mutate} />
    </Stack>
  );
}

const styles = {
  sidebarContainer: css(
    {
      height: "100%",
      borderLeft: `1px solid var(--border-base)`,
      width: "360px",
    },
    mq({
      height: ["600px", "600px", "100%"],
      width: ["100%", "100%", "360px"],
    })
  ),
  headerSection: css({
    padding: `var(--space-5) var(--space-6) var(--space-3) var(--space-6)`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
  }),
  contentSection: css({
    padding: `var(--space-0) var(--space-6)`,
    wordBreak: "break-word",
    overflowY: "scroll",
    maxHeight: "100%",
    display: "flex",
    flexDirection: "column",
    gap: `var(--space-6)`,
  }),
};
